import { Alert, Button, Group, Stack, Table, Text } from "@mantine/core";
import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import {
  ErrorState,
  LoadingSkeleton,
  SectionCard,
} from "../../components/ui";
import { api, type Reconciliation as Report } from "../../lib/api";
import { Page } from "../../shell/Page";

/**
 * Checks the stock figures against the ledger behind them.
 *
 * `products.currentStockQty` is a cache of the movement ledger, so the sum of
 * a product's entries must equal its quantity. That is stated in the schema as
 * an invariant, and an unchecked invariant about stock is just a hope - so
 * this screen actually asks.
 *
 * It reports and does not repair. A one-click "fix" would overwrite the
 * evidence of whatever caused the drift, and the cause is the thing worth
 * knowing: a mismatch means some code path moved a quantity without recording
 * why, and that path will do it again.
 */
export function Reconciliation() {
  const [report, setReport] = useState<Report>();
  const [error, setError] = useState<string>();
  const [checking, setChecking] = useState(false);

  async function run() {
    setChecking(true);
    setError(undefined);
    try {
      setReport(await api.reconcileStock());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not run the check.");
    } finally {
      setChecking(false);
    }
  }

  useEffect(() => {
    void run();
  }, []);

  const [copied, setCopied] = useState(false);

  /**
   * The drift, as plain text on the clipboard.
   *
   * Tab-separated so it lands in a spreadsheet or an email intact, and headed
   * with the date and the totals, because a list of SKUs with no date is not
   * evidence of anything a week later.
   */
  async function copyReport() {
    if (!report) return;
    const lines = [
      `Stock reconciliation — ${new Date().toLocaleString("en-IN")}`,
      `${report.checked} products checked, ${report.movements} with movement`,
      `${report.drifted.length} do not match their movement history`,
      "",
      ["SKU", "Product", "Shown", "Ledger", "Gap"].join("\t"),
      ...report.drifted.map((row) =>
        [
          row.sku,
          row.name,
          row.cached,
          row.fromLedger,
          `${row.cached - row.fromLedger > 0 ? "+" : ""}${row.cached - row.fromLedger}`,
        ].join("\t"),
      ),
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // A clipboard the browser refuses is not an error worth a dialog: the
      // table is on screen and can be read. Saying nothing would be worse
      // than saying so.
      notifications.show({
        color: "yellow",
        message: "This browser would not let the page copy. The table above is the report.",
      });
    }
  }

  return (
    <Page
      title="Reconciliation"
      subtitle="Stock figures against the ledger"
      back="/inventory"
      width="wide"
    >
      {error && <ErrorState message={error} onRetry={() => void run()} />}
      {!report && !error && <LoadingSkeleton variant="detail" />}

      {report && (
        <>
          <SectionCard title="The check">
            <Stack gap="sm">
              {report.balanced ? (
                <Alert
                  variant="light"
                  color="teal"
                  radius="md"
                  icon={<IconCircleCheck size={18} stroke={1.8} />}
                >
                  Balanced. All {report.checked} products agree with their
                  movement history.
                </Alert>
              ) : (
                <Alert
                  variant="light"
                  color="red"
                  radius="md"
                  icon={<IconAlertTriangle size={18} stroke={1.8} />}
                >
                  {report.drifted.length} product(s) do not match their
                  movement history.
                </Alert>
              )}
              <Stack gap={6} component="dl" m={0}>
                <Row label="Products checked" value={report.checked} />
                <Row label="With movement" value={report.movements} />
              </Stack>
              <Group gap="sm">
                <Button variant="default" loading={checking} onClick={() => void run()}>
                  Check again
                </Button>
                {/* COPY, not fix. Nothing here repairs a gap on purpose - see
                    the note below - so the action this screen offers is the
                    one that lets somebody take the discrepancy to whoever can
                    explain it. */}
                <Button
                  variant="default"
                  disabled={report.balanced}
                  onClick={() => void copyReport()}
                >
                  {copied ? "Copied" : "Copy this report"}
                </Button>
              </Group>
            </Stack>
          </SectionCard>

          {!report.balanced && (
            <SectionCard title="Where they differ">
              <Stack gap="md">
                <Table.ScrollContainer minWidth={420}>
                  <Table verticalSpacing="xs" horizontalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Item</Table.Th>
                        <Table.Th ta="right">Shown</Table.Th>
                        <Table.Th ta="right">Ledger</Table.Th>
                        <Table.Th ta="right">Gap</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {report.drifted.map((row) => (
                        <Table.Tr key={row.id}>
                          <Table.Td>
                            <Text fz="sm" c="gray.9" className="num">
                              {row.sku}
                            </Text>
                            <Text fz="xs" c="gray.6">
                              {row.name}
                            </Text>
                          </Table.Td>
                          <Table.Td ta="right" className="num">
                            {row.cached}
                          </Table.Td>
                          <Table.Td ta="right" className="num">
                            {row.fromLedger}
                          </Table.Td>
                          <Table.Td ta="right" className="num" fw={700}>
                            {row.drift > 0 ? "+" : ""}
                            {row.drift}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
                <Alert
                  variant="light"
                  color="yellow"
                  radius="md"
                  icon={<IconAlertTriangle size={18} stroke={1.8} />}
                >
                  Nothing is corrected automatically, on purpose. A gap here
                  means something changed a quantity without recording why —
                  fixing the number would hide the cause, and the cause will
                  recur. Report this before adjusting anything.
                </Alert>
              </Stack>
            </SectionCard>
          )}
        </>
      )}
    </Page>
  );
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <Group justify="space-between" gap="md" wrap="nowrap">
      <Text component="dt" fz="sm" c="gray.6">
        {label}
      </Text>
      <Text component="dd" fz="sm" fw={500} c="gray.9" m={0} className="num">
        {value}
      </Text>
    </Group>
  );
}
